import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form3922Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 3922 instructions for Tax Year 2025"
          description="Learn IRS Form 3922 step-by-step instructions for Tax Year 2025. TaxZerone guides you to accurately report employee stock purchase plan transfers."
          keywords=""
          canonical="resources/information-returns/form-3922-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form 3922</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report ESPP stock transfers accurately!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3922instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-3922-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 3922 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>
                          Form 3922, Transfer of Stock Acquired Through an
                          Employee Stock Purchase Plan Under Section 423(c)
                        </strong>
                        {
                          ', is used by corporations to report the transfer of stock '
                        }
                        acquired
                        {
                          ' by an employee through an employee stock purchase plan (ESPP) that qualifies under Section 423 of the Internal Revenue Code.'
                        }
                      </span>
                    </li>
                    <li>
                      <span>
                        {'Recipient copies must be furnished by '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>
                        {', the IRS filing deadline is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li>
                      <span>
                        {
                          'This article covers line-by-line instructions, including corporation and employee information, '
                        }
                        <strong style={{ fontWeight: 600 }}>{'Box 1 '}</strong>
                        (Date Option Granted)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong>
                        {' (Date Option Exercised)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 3 '}</strong>
                        (Fair Market Value per Share on Grant Date)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>
                        (Fair Market Value per Share on exercise Date){','}{' '}
                        <strong style={{ fontWeight: 600 }}>Box</strong>
                        <strong style={{ fontWeight: 600 }}>{' 5'}</strong>
                        {' (Exercise Price Paid Per Share)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Number of Shares Transferred)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 7</strong>
                        {' (Date Legal Title Transferred)'}
                        {', and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 8 '}</strong>
                        (Exercise Price Per
                        <strong style={{ fontWeight: 600 }}> </strong>
                        Share Determined as if the Option Was Exercised on the
                        Date Shown in Box 1).
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or incomplete filing of Form 3922 may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on when the return is filed, whether
                        corrections are required, and whether the filer complies
                        with electronic filing requirements.
                      </span>
                    </li>
                    <li>
                      <span>
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you’re a corporation transferring stock to an employee
                  through an Employee Stock Purchase Plan (ESPP), you must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3922/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file IRS Form 3922
                  </Link>
                  . This form reports the transfer of stock acquired under an
                  ESPP and provides important tax information for both the
                  employee and the IRS. Filing accurately and on time ensures
                  compliance with reporting requirements.
                </Typography>

                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form 3922?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to file Form 3922?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When is Form 3922 due?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#form"
                          id="whatis"
                          sx={{ color: '#0202ff' }}
                        >
                          Form 3922 instructions-how to fill out? online?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form 3922?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  IRS Form 3922 is an information return used by corporations to
                  report when an employee purchases stock through an Employee
                  Stock Purchase Plan (ESPP) at a discounted price. This form is
                  required by the IRS to help ensure transparency and accurate
                  tax reporting for stock-based compensation.
                </Typography>
                <Typography variant="body1" id="who" sx={{ mb: 4 }}>
                  It provides essential details like the date the option was
                  granted, the purchase date, the fair market value of the
                  stock, and the number of shares transferred. These details
                  help employees, and the IRS track the cost basis of the stock
                  for future tax purposes.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form 3922?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If a corporation transfers stock to an employee who exercised
                  an option under an Employee Stock Purchase Plan (ESPP), and
                  the stock was bought at a discount or the price wasn’t fixed
                  at the time of the offer, the corporation must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3922/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 3922
                  </Link>{' '}
                  for each transfer made during that year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  That &quot;first transfer&quot; includes situations like:{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    When the stock is initially transferred into the employee’s
                    name.{' '}
                  </li>
                  <li>
                    When the shares are first moved into a{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      brokerage account, even if it happens automatically
                    </span>{' '}
                    as part of a plan with a broker or financial
                    institution.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  When stock is transferred after an employee exercises an
                  option, the shares must be clearly identifiable to ensure
                  accurate reporting to the IRS.{' '}
                </Typography>
                <Alert severity="warning" icon={false} sx={{ mt: 2, mb: 4 }}>
                  <b>Note:</b> The corporation that provides the stock to the
                  employee is responsible for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-3922/"
                    sx={{ color: '#0202ff' }}
                    id="when"
                  >
                    {' '}
                    filing Form 3922
                  </Link>
                  .
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When Is Form 3922 Due?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The filing deadline for Form 3922 is as follows:
                </Typography>

                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Form 3922
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Sending copy to Employee
                          </TableCell>

                          <TableCell sx={{}}>February 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS e-filing
                          </TableCell>

                          <TableCell sx={{}} id="form">
                            March 31, 2026{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Form 3922 instructions-how to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let see the step-by-step instructions to fill out Form 3922
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Corporation’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-corporation-img.webp" />
                  <source srcSet="/assets/images/f3922-corporation-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-corporation-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Corporation’s details"
                    width={525}
                    height={208}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the corporation&apos;s name, address and Taxpayer
                  Identification Number (TIN) of the entity responsible for
                  issuing the stock through ESPP.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Employee’s details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f3922-employee-img.webp" />
                  <source srcSet="/assets/images/f3922-employee-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-employee-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Employee’s details"
                    width={524}
                    height={232}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name, address and Taxpayer Identification Number
                  (TIN) or Social Security Number (SSN) of the employee who
                  received the stock after exercising their option under the
                  Employee Stock Purchase Plan (ESPP).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Stock Option & Transfer details
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Account Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-account-img.webp" />
                  <source srcSet="/assets/images/f3922-account-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-account-img.png"
                    loading="lazy"
                    alt="Account Number"
                    width={525}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you&apos;re filing more than one Form 3922 for the same
                  person, you must include an account number to identify each
                  form. Even if not required, the IRS recommends using an
                  account number on all Forms 3922 to help with accurate
                  tracking and recordkeeping.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Box 1. Date Option Granted
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-date-granted-img.webp" />
                  <source srcSet="/assets/images/f3922-date-granted-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-date-granted-img.png"
                    loading="lazy"
                    alt="Box 1. Date Option Granted"
                    width={217}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date on which the corporation granted the employee
                  the option to purchase stock under an Employee Stock Purchase
                  Plan (ESPP).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Box 2. Date Option Exercised
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f3922-date-option-img.webp" />
                  <source srcSet="/assets/images/f3922-date-option-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-date-option-img.png"
                    loading="lazy"
                    alt="Box 2. Date Option Exercised"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date the employee exercised their option to purchase
                  stock through the ESPP.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3. Fair Market Value Per Share on Grant Date
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-grant-date-img.webp" />
                  <source srcSet="/assets/images/f3922-grant-date-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-grant-date-img.png"
                    loading="lazy"
                    alt="Box 3. Fair Market Value Per Share on Grant Date "
                    width={217}
                    height={79}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the fair market value (FMV) of each share on the date
                  the option to purchase the stock was granted.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4. Fair Market value per share on exercise date
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f3922-fair-market-img.webp" />
                    <source srcSet="/assets/images/f3922-fair-market-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f3922-fair-market-img.png"
                      loading="lazy"
                      alt="Box 4. Fair Market value per share on exercise date"
                      width={217}
                      height={79}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the fair market value per share of the stock on the date
                  the employee exercised their option to purchase it.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5. Exercise Price Paid Per Share
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/f3922-paid-per-img.webp" />
                    <source srcSet="/assets/images/f3922-paid-per-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/f3922-paid-per-img.png"
                      loading="lazy"
                      alt="Box 5. Exercise Price Paid Per Share"
                      width={217}
                      height={53}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the price in which the employee paid for each share when
                  they purchased the stock through the option.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6. Number of Shares Transferred
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-number-share-img.webp" />
                  <source srcSet="/assets/images/f3922-number-share-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-number-share-img.png"
                    loading="lazy"
                    alt="Box 6. Number of Shares Transferred"
                    width={217}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total number of shares for which legal ownership was
                  transferred to the employee.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7. Date Legal Title Transferred
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f3922-date-legal-img.webp" />
                  <source srcSet="/assets/images/f3922-date-legal-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-date-legal-img.png"
                    loading="lazy"
                    alt="Box 7. Date Legal Title Transferred"
                    width={217}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the date when the employee first gained legal ownership
                  of the shares.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8. Exercise Price Per Share Determined as if the Option
                  Was Exercised on the Date Shown in Box 1
                </Typography>

                <picture>
                  <source srcSet="/assets/images/f3922-exercise-img.webp" />
                  <source srcSet="/assets/images/f3922-exercise-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f3922-exercise-img.png"
                    loading="lazy"
                    alt="Box 8. Exercise Price Per Share"
                    width={432}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the exercise price per share was not set or determinable on
                  the option grant date (Box 1), enter the price as if the
                  option were exercised on the grant date. If the exercise price
                  is fixed or determinable on the grant date, leave Box 8 blank.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on the IRS
                        guidelines. For more details, please refer to the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/i3921.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instructions on Form 3922
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          General instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file Form 3922?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Ensure timely and accurate reporting of your ESPP stock transfers.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=3922instructions`}
              >
                Start filing Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form3922Instructions;
